import { useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  CheckCircle,
  Circle,
  Loader2,
  XCircle,
  Clock,
  PlayCircle,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Progress } from '@/components/ui/Progress';
import { cn } from '@/utils/cn';
import { conversationsApi } from '@/api/conversations';
import { useToast } from '@/contexts/ToastContext';
import type { WorkPlan, WorkPlanPhase, WorkPlanTask } from '@/types';

interface WorkPlanViewProps {
  workPlan: WorkPlan;
  onApprove?: () => void;
  onReject?: (reason: string) => void;
}

export function WorkPlanView({ workPlan, onApprove, onReject }: WorkPlanViewProps) {
  const [expanded, setExpanded] = useState(true);
  const toast = useToast();

  const completedPhases = workPlan.phases.filter(
    (p) => p.status === 'Completed'
  ).length;
  const totalPhases = workPlan.phases.length;
  const progress = totalPhases > 0 ? (completedPhases / totalPhases) * 100 : 0;

  const handleApprovePhase = async (phaseId: string) => {
    try {
      await conversationsApi.approvePhase(phaseId);
      toast.success('Phase Approved', 'The phase has been approved and will continue.');
    } catch (error) {
      toast.error('Error', 'Failed to approve phase');
    }
  };

  const handleRejectPhase = async (phaseId: string, reason: string) => {
    try {
      await conversationsApi.rejectPhase(phaseId, reason);
      toast.info('Phase Rejected', 'The phase has been rejected.');
    } catch (error) {
      toast.error('Error', 'Failed to reject phase');
    }
  };

  return (
    <div className="bg-gray-50 dark:bg-dark-900">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-100 dark:hover:bg-dark-800 transition-colors"
      >
        <div className="flex items-center gap-3">
          {expanded ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
          <div>
            <h3 className="font-medium text-gray-900 dark:text-white text-left">
              Work Plan
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {completedPhases} of {totalPhases} phases complete
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <WorkPlanStatusBadge status={workPlan.status} />
          <div className="w-32">
            <Progress value={progress} size="sm" />
          </div>
        </div>
      </button>

      {/* Content */}
      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          {workPlan.phases.map((phase, index) => (
            <PhaseCard
              key={phase.id}
              phase={phase}
              index={index}
              onApprove={() => handleApprovePhase(phase.id)}
              onReject={(reason) => handleRejectPhase(phase.id, reason)}
            />
          ))}

          {/* Actions for pending approval */}
          {workPlan.status === 'AwaitingApproval' && (
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-dark-700">
              <Button
                variant="outline"
                onClick={() => onReject?.('User rejected the work plan')}
              >
                Reject Plan
              </Button>
              <Button onClick={onApprove}>Approve & Execute</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PhaseCard({
  phase,
  index,
  onApprove,
  onReject,
}: {
  phase: WorkPlanPhase;
  index: number;
  onApprove: () => void;
  onReject: (reason: string) => void;
}) {
  const [expanded, setExpanded] = useState(
    phase.status === 'InProgress' || phase.status === 'AwaitingApproval'
  );

  return (
    <div
      className={cn(
        'rounded-lg border overflow-hidden',
        phase.status === 'InProgress'
          ? 'border-primary-300 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/10'
          : phase.status === 'Failed'
          ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/10'
          : 'border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-800'
      )}
    >
      {/* Phase Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-dark-700/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <PhaseStatusIcon status={phase.status} />
          <div className="text-left">
            <p className="font-medium text-gray-900 dark:text-white">
              Phase {index + 1}: {phase.name}
            </p>
            {phase.description && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {phase.description}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {phase.requiresApproval && phase.status === 'Pending' && (
            <span className="px-2 py-0.5 text-xs font-medium rounded bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
              Needs Approval
            </span>
          )}
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </button>

      {/* Phase Content */}
      {expanded && (
        <div className="px-3 pb-3 space-y-2">
          {phase.tasks.map((task, taskIndex) => (
            <TaskItem key={task.id} task={task} index={taskIndex} />
          ))}

          {/* Approval Actions */}
          {phase.status === 'AwaitingApproval' && (
            <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-200 dark:border-dark-700">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onReject('User rejected this phase')}
              >
                Reject
              </Button>
              <Button size="sm" onClick={onApprove}>
                Approve
              </Button>
            </div>
          )}

          {/* Error Message */}
          {phase.status === 'Failed' && phase.errorMessage && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700 dark:text-red-400">
                  {phase.errorMessage}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TaskItem({ task, index }: { task: WorkPlanTask; index: number }) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 p-2 rounded-lg',
        task.status === 'InProgress' && 'bg-primary-50 dark:bg-primary-900/10',
        task.status === 'Failed' && 'bg-red-50 dark:bg-red-900/10'
      )}
    >
      <TaskStatusIcon status={task.status} />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-900 dark:text-white">{task.description}</p>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
            {task.taskType}
          </span>
          {task.status === 'InProgress' && (
            <span className="text-xs text-primary-600 dark:text-primary-400">
              Running...
            </span>
          )}
          {task.status === 'Completed' && task.outputData && (
            <span className="text-xs text-green-600 dark:text-green-400">
              {task.outputData}
            </span>
          )}
          {task.status === 'Failed' && task.errorMessage && (
            <span className="text-xs text-red-600 dark:text-red-400">
              {task.errorMessage}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function PhaseStatusIcon({ status }: { status: string }) {
  switch (status) {
    case 'Completed':
      return <CheckCircle className="w-5 h-5 text-green-500" />;
    case 'InProgress':
      return <Loader2 className="w-5 h-5 text-primary-500 animate-spin" />;
    case 'Failed':
      return <XCircle className="w-5 h-5 text-red-500" />;
    case 'AwaitingApproval':
      return <Clock className="w-5 h-5 text-yellow-500" />;
    default:
      return <Circle className="w-5 h-5 text-gray-300 dark:text-gray-600" />;
  }
}

function TaskStatusIcon({ status }: { status: string }) {
  switch (status) {
    case 'Completed':
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    case 'InProgress':
      return <Loader2 className="w-4 h-4 text-primary-500 animate-spin" />;
    case 'Failed':
      return <XCircle className="w-4 h-4 text-red-500" />;
    default:
      return <Circle className="w-4 h-4 text-gray-300 dark:text-gray-600" />;
  }
}

function WorkPlanStatusBadge({ status }: { status: string }) {
  const configs: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
    Pending: {
      bg: 'bg-gray-100 dark:bg-dark-700',
      text: 'text-gray-600 dark:text-gray-400',
      icon: <Circle className="w-3 h-3" />,
    },
    InProgress: {
      bg: 'bg-primary-100 dark:bg-primary-900/30',
      text: 'text-primary-600 dark:text-primary-400',
      icon: <PlayCircle className="w-3 h-3" />,
    },
    AwaitingApproval: {
      bg: 'bg-yellow-100 dark:bg-yellow-900/30',
      text: 'text-yellow-600 dark:text-yellow-400',
      icon: <Clock className="w-3 h-3" />,
    },
    Completed: {
      bg: 'bg-green-100 dark:bg-green-900/30',
      text: 'text-green-600 dark:text-green-400',
      icon: <CheckCircle className="w-3 h-3" />,
    },
    Failed: {
      bg: 'bg-red-100 dark:bg-red-900/30',
      text: 'text-red-600 dark:text-red-400',
      icon: <XCircle className="w-3 h-3" />,
    },
    Cancelled: {
      bg: 'bg-gray-100 dark:bg-dark-700',
      text: 'text-gray-600 dark:text-gray-400',
      icon: <XCircle className="w-3 h-3" />,
    },
  };

  const config = configs[status] || configs.Pending;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full',
        config.bg,
        config.text
      )}
    >
      {config.icon}
      {status}
    </span>
  );
}
